<h2 mat-dialog-title>{{dialogTitle}}</h2>

<mat-dialog-content class="dialog-content">
  @if (loading) {
    <div class="state-message">Loading linked accounts, teams, and templates...</div>
  }
  @if (!loading && loadError) {
    <div class="state-message error">{{loadError}}</div>
  }

  @if (!loading && !loadError) {
    <form class="dialog-grid" [formGroup]="form">
      <div class="section">
        <h3>Target</h3>
        <div class="row two-col">
          <mat-form-field appearance="outline">
            <mat-label>Linked Plaid Account</mat-label>
            <mat-select formControlName="targetAccountId">
              @for (account of accounts; track account) {
                <mat-option [value]="account.accountId">
                  {{account.name}}@if (account.mask) {
                  <span> •••{{account.mask}}</span>
                  }@if (account.institutionName) {
                  <span> - {{account.institutionName}}</span>
                }
              </mat-option>
            }
          </mat-select>
          @if (form.get('targetAccountId')?.touched && form.get('targetAccountId')?.invalid) {
            <mat-error>
              Choose a linked account.
            </mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Team Context</mat-label>
          <mat-select formControlName="teamId">
            @for (team of teams; track team) {
              <mat-option [value]="team.id">{{team.name}}</mat-option>
            }
          </mat-select>
          @if (form.get('teamId')?.touched && form.get('teamId')?.invalid) {
            <mat-error>
              Choose a team.
            </mat-error>
          }
        </mat-form-field>
      </div>
    </div>
    <div class="section">
      <div>
        <h3>Add Transactions</h3>
        <div class="result-meta">Use bundles, custom transactions, or both. They all become the same tool-generated sandbox data after apply.</div>
      </div>
      <div class="composer-grid">
        <div class="template-card">
          <div class="template-header">
            <div>
              <div class="template-title">From Bundles</div>
              <div class="template-purpose">Pick one or more predefined reimbursement scenarios.</div>
            </div>
          </div>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Search bundles</mat-label>
            <input matInput [formControl]="templateSearchControl" placeholder="Search by label, purpose, or tag">
          </mat-form-field>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Bundles</mat-label>
            <mat-select formControlName="templateIds" multiple>
              @for (template of filteredTemplates; track template) {
                <mat-option [value]="template.id">
                  {{template.label}}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          @if (selectedTemplates.length) {
            <div class="tag-list">
              @for (template of selectedTemplates; track template) {
                <span class="tag">{{template.label}}</span>
              }
            </div>
          }
        </div>
        <div class="template-card">
          <div class="results-header">
            <div>
              <div class="template-title">Custom Transactions</div>
              <div class="template-purpose">Add one-off Plaid-like transactions with the same sandbox lifecycle as bundles.</div>
            </div>
            <div class="results-actions">
              <button mat-stroked-button type="button" (click)="addCustomTransaction()">Add Transaction</button>
            </div>
          </div>
          @if (!customTransactionControls.length) {
            <div class="state-message inline-state-message">
              No custom transactions added yet.
            </div>
          }
          @if (customTransactionControls.length) {
            <div formArrayName="customTransactions" class="custom-transaction-list">
              @for (customTransaction of customTransactionControls; track customTransaction; let index = $index) {
                <div
                  class="custom-transaction-card"
                  [formGroupName]="index">
                  <div class="custom-transaction-header">
                    <div class="template-title">Custom {{index + 1}}</div>
                    <button mat-icon-button type="button" (click)="removeCustomTransaction(index)" aria-label="Remove custom transaction">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>
                  <div class="custom-transaction-fields">
                    <div class="field-stack field-span-2">
                      <div class="field-name">Merchant</div>
                      <mat-form-field appearance="outline" class="full-width">
                        <input matInput formControlName="merchant" maxlength="45" placeholder="DoorDash Order">
                      </mat-form-field>
                    </div>
                    <div class="field-stack">
                      <div class="field-name">Reimbursement Category</div>
                      <mat-form-field appearance="outline" class="full-width">
                        <mat-select formControlName="teamCategoryName">
                          @for (category of categories; track category) {
                            <mat-option [value]="category.name">{{category.name}}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    </div>
                    <div class="field-stack field-span-2">
                      <div class="override-row">
                        <mat-checkbox formControlName="useSeedCategoryOverride">Store different category</mat-checkbox>
                        <button
                          mat-icon-button
                          type="button"
                          class="info-button"
                          [matTooltip]="seedCategoryOverrideHelpText"
                          aria-label="Explain stored category override">
                          <mat-icon>info</mat-icon>
                        </button>
                      </div>
                      @if (customTransaction.get('useSeedCategoryOverride')?.value) {
                        <div class="field-help">
                          The reimbursement category above remains the test intent. This override changes the stored transaction category written to the generated record.
                        </div>
                      }
                    </div>
                    @if (customTransaction.get('useSeedCategoryOverride')?.value) {
                      <div class="field-stack">
                        <div class="field-name">Stored Transaction Category</div>
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-select formControlName="seedCategoryName">
                            @for (category of categories; track category) {
                              <mat-option [value]="category.name">{{category.name}}</mat-option>
                            }
                          </mat-select>
                          @if (customTransaction.get('seedCategoryName')?.touched && customTransaction.get('seedCategoryName')?.invalid) {
                            <mat-error>
                              Choose the stored category to write.
                            </mat-error>
                          }
                        </mat-form-field>
                      </div>
                    }
                    <div class="field-stack">
                      <div class="field-name">Amount</div>
                      <mat-form-field appearance="outline" class="full-width">
                        <input matInput type="number" min="0.01" step="0.01" formControlName="amount">
                      </mat-form-field>
                    </div>
                    <div class="field-stack">
                      <div class="field-name">Days Ago</div>
                      <mat-form-field appearance="outline" class="full-width">
                        <input matInput type="number" min="0" step="1" formControlName="daysAgo">
                      </mat-form-field>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </div>
      @if (form.touched && form.hasError('scenarioSelectionRequired')) {
        <div class="state-message error">
          Select at least one bundle or add at least one custom transaction.
        </div>
      }
      <mat-slide-toggle formControlName="wipeBeforeApply">
        Wipe tool-generated data on this account before applying
      </mat-slide-toggle>
    </div>
    @if (previewRows.length) {
      <div class="section">
        <h3>Transactions To Apply</h3>
        <div class="template-card">
          <table class="template-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Merchant</th>
                <th>Reimbursement Category</th>
                <th>Stored Category</th>
                <th>Amount</th>
                <th>Days Ago</th>
              </tr>
            </thead>
            <tbody>
              <!-- Preview rows are derived and identical transactions are valid. -->
              @for (transaction of previewRows; track $index) {
                <tr>
                  <td>{{transaction.sourceLabel}}</td>
                  <td>{{transaction.merchant}}</td>
                  <td>{{transaction.teamCategoryName}}</td>
                  <td>{{transaction.seedCategoryName || transaction.teamCategoryName}}</td>
                  <td>{{transaction.amount | currency}}</td>
                  <td>{{transaction.daysAgo}}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
    <div class="section">
      <div class="results-header">
        <div>
          <h3>Current Tool-Generated Data</h3>
          @if (currentAccountData?.latestScenarioRunId) {
            <div class="result-meta">
              Latest run: <code>{{currentAccountData?.latestScenarioRunId}}</code>
            </div>
          }
        </div>
        <div class="results-actions">
          <button mat-stroked-button type="button" (click)="refreshScenarioRun()" [disabled]="!latestResult || applyInFlight">
            Refresh Latest Run
          </button>
          <button mat-stroked-button type="button" (click)="refreshCurrentAccountData()" [disabled]="currentDataLoading">
            Refresh Current Data
          </button>
        </div>
      </div>
      @if (currentDataLoading) {
        <div class="state-message">Loading current tool-generated data...</div>
      }
      @if (!currentDataLoading && currentAccountData && !currentAccountData.hasToolGeneratedData) {
        <div class="state-message">
          No tool-generated transactions are currently attached to this account.
        </div>
      }
      @if (!currentDataLoading && currentAccountData?.hasToolGeneratedData) {
        <div>
          <div class="summary-grid">
            <div class="summary-card">
              <div class="summary-label">Scenario Runs</div>
              <div class="summary-value">{{currentAccountData?.totalScenarioRuns}}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Transactions</div>
              <div class="summary-value">{{currentAccountData?.totalTransactions}}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Pending</div>
              <div class="summary-value">{{currentAccountData?.statusCounts?.pending}}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">Visible In Home</div>
              <div class="summary-value">{{currentAccountData?.statusCounts?.visibleInHomeReimbursements}}</div>
            </div>
          </div>
          @if (currentAccountData?.verification?.issues?.length) {
            <div class="issues">
              <h4>Current Verification Issues</h4>
              @for (issue of currentAccountData?.verification?.issues; track issue) {
                <div class="issue">
                  {{issue.name}} ({{issue.transactionId}}): expected {{issue.expectedBehavior}} in {{issue.expectedTeamCategoryName}}, actual category {{issue.actualTeamCategoryName || 'unknown'}}, visible={{issue.actualHomeVisibility}}
                </div>
              }
            </div>
          }
          @for (scenarioRun of currentAccountData?.scenarioRuns; track scenarioRun) {
            <div class="current-run-card">
              <div class="template-header">
                <div>
                  <div class="template-title">Run {{scenarioRun.scenarioRunId}}</div>
                  <div class="template-purpose">
                    {{scenarioRun.transactionCount}} transaction(s)
                    @if (scenarioRun.teamId) {
                      <span> • Team {{scenarioRun.teamId}}</span>
                    }
                    @if (scenarioRun.latestTransactionDate) {
                      <span> • Latest {{scenarioRun.latestTransactionDate | date: 'mediumDate'}}</span>
                    }
                  </div>
                </div>
                <div class="tag-list">
                  @for (templateId of scenarioRun.templateIds; track templateId) {
                    <span class="tag">{{templateId}}</span>
                  }
                </div>
              </div>
              <div class="mini-summary">
                <span>Expected visible: {{scenarioRun.verification.expectedHomeVisibleCount}}</span>
                <span>Actual visible: {{scenarioRun.verification.actualHomeVisibleCount}}</span>
                <span [class.error-text]="scenarioRun.verification.failedCount > 0">Failed: {{scenarioRun.verification.failedCount}}</span>
              </div>
              <table class="result-table">
                <thead>
                  <tr>
                    <th>Merchant</th>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Pending</th>
                    <th>Home Visible</th>
                  </tr>
                </thead>
                <tbody>
                  @for (transaction of scenarioRun.transactions; track transaction) {
                    <tr>
                      <td>{{transaction.name}}</td>
                      <td>{{transaction.actualTeamCategoryName || transaction.verification.expectedTeamCategoryName}}</td>
                      <td>{{transaction.amount | currency}}</td>
                      <td>{{transaction.transactionDate | date: 'mediumDate'}}</td>
                      <td>{{transaction.pending ? 'Yes' : 'No'}}</td>
                      <td>{{transaction.visibleInHomeReimbursements ? 'Yes' : 'No'}}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      }
    </div>
    @if (actionError) {
      <div class="section">
        <div class="state-message error">{{actionError}}</div>
      </div>
    }
    @if (latestResult) {
      <div class="section">
        <div class="results-header">
          <div>
            <h3>Latest Result</h3>
            <div class="result-meta">Run: <code>{{latestResult.scenarioRunId}}</code></div>
          </div>
          <div class="results-actions">
            <button mat-stroked-button type="button" (click)="refreshScenarioRun()" [disabled]="applyInFlight">
              Refresh
            </button>
            <button mat-stroked-button color="warn" type="button" (click)="wipeScenario('selected_run')" [disabled]="wipeInFlight">
              Wipe Selected Run
            </button>
            <button mat-stroked-button color="warn" type="button" (click)="wipeScenario('all_tool_generated_for_account')" [disabled]="wipeInFlight">
              Wipe All Tool Data
            </button>
          </div>
        </div>
        <div class="summary-grid">
          <div class="summary-card">
            <div class="summary-label">Transactions</div>
            <div class="summary-value">{{latestResult.verification.totalTransactions}}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Expected Visible</div>
            <div class="summary-value">{{latestResult.verification.expectedHomeVisibleCount}}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Actual Visible</div>
            <div class="summary-value">{{latestResult.verification.actualHomeVisibleCount}}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Failed Checks</div>
            <div class="summary-value" [class.error-text]="latestResult.verification.failedCount > 0">
              {{latestResult.verification.failedCount}}
            </div>
          </div>
        </div>
        @if (latestResult.verification.issues.length) {
          <div class="issues">
            <h4>Verification Issues</h4>
            @for (issue of latestResult.verification.issues; track issue) {
              <div class="issue">
                {{issue.name}} ({{issue.transactionId}}): expected {{issue.expectedBehavior}} in {{issue.expectedTeamCategoryName}}, actual category {{issue.actualTeamCategoryName || 'unknown'}}, visible={{issue.actualHomeVisibility}}
              </div>
            }
          </div>
        }
        <table class="result-table">
          <thead>
            <tr>
              <th>Merchant</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Pending</th>
              <th>Home Visible</th>
              <th>Check</th>
            </tr>
          </thead>
          <tbody>
            @for (transaction of latestResult.transactions; track transaction) {
              <tr>
                <td>{{transaction.name}}</td>
                <td>{{transaction.actualTeamCategoryName || transaction.verification.expectedTeamCategoryName}}</td>
                <td>{{transaction.amount | currency}}</td>
                <td>{{transaction.transactionDate | date: 'mediumDate'}}</td>
                <td>{{transaction.pending ? 'Yes' : 'No'}}</td>
                <td>{{transaction.visibleInHomeReimbursements ? 'Yes' : 'No'}}</td>
                <td [class.error-text]="!transaction.verification.passed">{{transaction.verification.passed ? 'Pass' : 'Fail'}}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
    @if (latestWipeResult) {
      <div class="section">
        <h3>Latest Wipe</h3>
        <div class="result-meta">
          Mode: <code>{{latestWipeResult.wipeMode}}</code>
          @if (latestWipeResult.scenarioRunId) {
            <span> • Run: <code>{{latestWipeResult.scenarioRunId}}</code></span>
          }
          • Deleted: {{latestWipeResult.deletedCount}}
        </div>
      </div>
    }
  </form>
}
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" (click)="wipeScenario('last_run')" [disabled]="loading || wipeInFlight">
    Wipe Last Run
  </button>
  <button mat-stroked-button type="button" (click)="close()">Close</button>
  <button mat-flat-button color="primary" type="button" (click)="applyScenario()" [disabled]="loading || applyInFlight">
    Apply Scenario
  </button>
</mat-dialog-actions>
